import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Autocomplete from "react-google-autocomplete";
import { InputGroup, Button } from "reactstrap";
import { Address, createAddressFromPlacesResult, resolveAddressFromText } from "models/address";
import { SessionStorageWrapper } from "models/session-storage-wapper";
import { selectedLocationKey } from "models/session-storage-keys";
import { EstimateApi } from "api/api";

export default function SelectLocationPage() {
  const navigate = useNavigate();
  const { portalId } = useParams();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  // Enter can fire both Google's place_changed and the form submit; only the first one proceeds.
  const submittingRef = React.useRef(false);
  const [error, setError] = React.useState<string>();
  const [keyboardSpace, setKeyboardSpace] = React.useState(false);

  const finishWithError = (message: string) => {
    submittingRef.current = false;
    setBusy(false);
    setError(message);
  };

  const startEstimate = (selectedAddress: Address) => {
    SessionStorageWrapper.addItem(selectedLocationKey, selectedAddress);
    EstimateApi.createEstimate(selectedAddress)
      .then((r) => navigate(`/${portalId}/estimate/${r.estimateId}`))
      .catch(() => {
        finishWithError("Something went wrong starting your estimate. Please try again.");
      });
  };

  const beginSubmit = () => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setError(undefined);
    setBusy(true);
    return true;
  };

  const onPlaceSelect = (place: google.maps.places.PlaceResult) => {
    const selectedAddress = createAddressFromPlacesResult(place);
    // A place without an address (Enter with no suggestion picked) is handled by the form submit.
    if (selectedAddress !== undefined && beginSubmit()) startEstimate(selectedAddress);
  };

  const submitTypedAddress = async () => {
    const text = inputRef.current?.value ?? "";
    if (text.trim() === "") {
      setError("Please enter your property address.");
      return;
    }
    if (!beginSubmit()) return;
    const selectedAddress = await resolveAddressFromText(text);
    if (selectedAddress === undefined) {
      finishWithError("We couldn't find that address. Please check it and try again.");
      return;
    }
    startEstimate(selectedAddress);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitTypedAddress();
  };

  // On touch devices the on-screen keyboard covers the lower half of the screen, hiding the
  // suggestion list under the vertically-centered input. Add room below and bring the input to the
  // top instead. The extra space is never removed, so the layout can't shift under a tap.
  const onFocus = () => {
    if (!window.matchMedia?.("(pointer: coarse)").matches) return;
    setKeyboardSpace(true);
    setTimeout(() => inputRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }), 300);
  };

  return (
    <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center px-4 py-5 text-center">
      <span className="badge bg-secondary text-dark text-uppercase mb-3" style={{ letterSpacing: "0.7px" }}>
        Free Estimate Tool
      </span>
      <h1 className="display-5 fw-800 text-white">A new fence is just a click away</h1>
      <div className="col-lg-6 mx-auto">
        <p className="lead text-white-50 mb-4">
          Design your fence and get an instant price range — no salesperson required.
        </p>
        <form onSubmit={onSubmit}>
          <InputGroup className="mb-2">
            <Autocomplete
              ref={inputRef}
              placeholder="Enter your property address..."
              className="form-control"
              apiKey={process.env.REACT_APP_GMAPS_API_KEY}
              options={{ types: ["address"] }}
              onPlaceSelected={onPlaceSelect}
              onFocus={onFocus}
              libraries={["places"]}
              enterKeyHint="go"
              style={{ scrollMarginTop: "16px" }}
            />
            <Button type="submit" color="secondary" className="text-dark fw-bold" disabled={busy}>
              {busy ? "…" : "Go →"}
            </Button>
          </InputGroup>
        </form>
        {error && (
          <p role="alert" className="text-warning small">
            {error}
          </p>
        )}
        <p className="text-white-50 small">Takes less than 5 minutes</p>
      </div>
      {keyboardSpace && <div aria-hidden style={{ height: "60vh", flexShrink: 0 }} />}
    </div>
  );
}
